import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Subtitle } from "capitalrx-components-2.0/lib/typography";

import { ContentPanel, PageWrapper, ThemedDropdown } from "@/App/components";
import { ToggleButton, ToggleStatus } from "@/App/components/ToggleButton";
import { useIsDiscountUser, useIsMedicareUser, useIsUnifiedUser } from "@/App/hooks";
import { useAccumulations } from "@/App/hooks/accumulations";
import { Accumulations } from "@/App/pages/PlanContributions/Accumulations/Accumulations";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service";
import { DisplayNetworkStatuses, DisplayScopes } from "@/App/types/accumulation";
import { isFamilyCoverage } from "@/App/utils/familyCoverage";

const TRANSLATION_PATH = "PLAN_CONTRIBUTIONS_PAGE";

export const PlanContributionsPage: FC = () => {
  const { t: translate } = useTranslation();
  const { isDiscountUser } = useIsDiscountUser();
  const { isMedicareUser } = useIsMedicareUser();
  const { isUnifiedUser } = useIsUnifiedUser();

  const { accumulations, individualAccumulations, familyAccumulations } = useAccumulations();

  const displayScopeToggleVisible = useMemo(() => {
    return !!(individualAccumulations(accumulations?.results)?.length && familyAccumulations(accumulations?.results)?.length);
  }, [accumulations, individualAccumulations, familyAccumulations]);

  const [displayNetworkStatus, setDisplayNetworkStatus] = useState<DisplayNetworkStatuses>(DisplayNetworkStatuses.In);
  const [displayScope, setDisplayScope] = useState<DisplayScopes>(isFamilyCoverage ? DisplayScopes.Family : DisplayScopes.Individual);

  const scopes = {
    individual: translate("BENEFITS.commercialBenefits.displayScopeIndividual"),
    family: translate("BENEFITS.commercialBenefits.displayScopeFamily"),
  };

  const onChangeNetworkDisplayType = useCallback((value: ToggleStatus) => {
    if (value === "second") {
      setDisplayNetworkStatus(DisplayNetworkStatuses.Out);
      logEvent(ANALYTIC_EVENTS.MY_RX.outOfNetwork);
    } else {
      setDisplayNetworkStatus(DisplayNetworkStatuses.In);
      logEvent(ANALYTIC_EVENTS.MY_RX.inNetwork);
    }
  }, []);

  const onChangeScopeDisplayType = useCallback((value: string) => {
    if (value === DisplayScopes.Family) {
      setDisplayScope(DisplayScopes.Family);
      logEvent(ANALYTIC_EVENTS.MY_RX.family);
    } else {
      setDisplayScope(DisplayScopes.Individual);
      logEvent(ANALYTIC_EVENTS.MY_RX.individual);
    }
  }, []);

  return (
    <PageWrapper>
      <ContentPanel showBorder title={<Subtitle>{translate(`${TRANSLATION_PATH}.planContributions`)}</Subtitle>}>
        <Stack spacing="large">
          {displayScopeToggleVisible && (
            <Shelf>
              <ThemedDropdown
                testId="display-scope-test"
                onChange={(val: string) => onChangeScopeDisplayType(val as DisplayScopes)}
                value={displayScope}
                label={translate("BENEFITS.commercialBenefits.viewFor")}
                aria-label={displayScope}
              >
                {Object.values(DisplayScopes).map((scope) => (
                  <ThemedDropdown.Option value={scope} key={scope}>
                    {scopes[scope]}
                  </ThemedDropdown.Option>
                ))}
              </ThemedDropdown>
              <Shelf.Spacer />
            </Shelf>
          )}
          {!isDiscountUser && isUnifiedUser && (
            <ToggleButton
              onChange={onChangeNetworkDisplayType}
              firstText={translate("DASHBOARD.dashboardPage.inNetwork")}
              secondText={translate("DASHBOARD.dashboardPage.outOfNetwork")}
            />
          )}
          {!isMedicareUser && !isDiscountUser && (
            <>
              <Accumulations displayNetworkStatus={displayNetworkStatus} displayScope={displayScope} />
            </>
          )}
        </Stack>
      </ContentPanel>
    </PageWrapper>
  );
};
